import {FeatureFlagApiPayload, FeatureFlagApiResponse} from 'app_api/projects/feature_flags/types';
import {BoardFilter} from 'apps/boards/src/dashboards-app/components/global-filter-bar/types';
import {SerializedCohort} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {MetricDefinition} from 'common/react-query/metrics/types';
import {FilterClause} from 'common/report/insights/util/types';
import type {Content} from 'common/types/app-api/content';

export const enum ExperimentStatus {
  DRAFT = `draft`,
  CONFIGURED = `configured`,
  ACTIVE = `active`,
  CONCLUDED = `concluded`,
  SUCCESS = `success`,
  SUCCESS_DEFERRED = `success-deferred`,
  FAIL = `fail`,
}

export type ExperimentVariant = {
  id: string;
  name: string;
};

export const enum ExperimentMetricType {
  PRIMARY = `primary`,
  GUARDRAIL = `guardrail`,
  SECONDARY = `secondary`,
}

export const enum ExperimentEndConditionType {
  SAMPLE_SIZE = `sample_size`,
  DAYS = `days`,
}

export type ExperimentMetric = {
  id: string;
  name: string;
  type: ExperimentMetricType;
  definition: MetricDefinition;
  dataGroupId?: string;
  filters?: FilterClause[];
};

export const enum ExperimentCollectionType {
  FEATURE_FLAG = `feature_flag`,
  EXPOSURE_EVENTS = `exposure_events`,
}

export const enum ExperimentTestingModel {
  FREQUENTIST = `frequentist`,
  SEQUENTIAL = `sequential`,
}

export type ExperimentFeatureFlagSettings = {
  featureFlagId?: string;
  featureFlagKey?: string; // if set, will create new FF based on feature flag key
};

export type ExperimentExposureEventSettings = {
  experimentName: string;
};

export type ExperimentGlobalFilterSettings = {
  variantsFilter?: string;
  filters: BoardFilter[];
};

export type ExperimentSettings = {
  collectionMethod: ExperimentCollectionType;
  featureFlagSettings?: ExperimentFeatureFlagSettings;
  exposureEventSettings?: ExperimentExposureEventSettings;
  sampleSize?: number;
  confidenceLevel?: number;
  testingModel?: ExperimentTestingModel;
  controlKey?: string;
  endCondition?: ExperimentEndConditionType;
  endAfterDays?: number;
  globalFilters?: ExperimentGlobalFilterSettings;
  cohort?: SerializedCohort | null;
};

export type ExperimentExposuresCache = {
  [variant: string]: number;
};

export const NO_VARIANT_SHIPPED = `__no_variant_shipped__`;
export const DEFER_VARIANT_DECISION = `__defer_variant_decision__`;

export type ExperimentResultsCache = {
  message?: string;
  messageUpdatedAt?: string;
  messageUpdatedBy?: string;
  variant?: string;
  summary?: Partial<Record<StatisticallySignificant, FlattenedExperimentMetricResults[]>>;
};

export type Experiment = Content & {
  description?: null | string;
  hypothesis?: null | string;
  status: ExperimentStatus;
  metrics: ExperimentMetric[];
  settings: ExperimentSettings;
  exposures_cache: ExperimentExposuresCache;
  results_cache: ExperimentResultsCache;
  start_date?: string | null;
  end_date?: string | null;
  feature_flag?: FeatureFlagApiResponse;
};

export type ExperimentUpdates = Partial<Omit<Experiment, `settings`>> & {settings?: Partial<Experiment[`settings`]>};
export type FeatureFlagUpdates = Partial<Omit<FeatureFlagApiPayload, `ruleset`>> & {
  ruleset?: Partial<FeatureFlagApiPayload[`ruleset`]>;
};

export const enum StatisticallySignificant {
  YES_POSITIVE = `positive`,
  YES_NEGATIVE = `negative`,
  NO = `no`,
}

export type ExperimentMetricResultsFormat = `number` | `percent`;

export type ExperimentMetricResults = {
  control: number;
  controlFormat: ExperimentMetricResultsFormat;
  treatment: number;
  treatmentFormat: ExperimentMetricResultsFormat;
  lift: number;
  liftConfidence: number;
  pValue: number;
  statisticallySignificant: StatisticallySignificant;
  notSupported?: boolean;
};

export type FlattenedExperimentMetricResults = ExperimentMetricResults & {
  metricId: string;
  variant: string;
};

export type StatsigResults = {
  [variantKey: string]: {
    all?: number;
    count: number;
    exposures: number;
    format: ExperimentMetricResultsFormat;
    is_significant: `True` | `False`;
    lift: number;
    lift_margin_of_error: number;
    p_value: number;
    raw_data?: number;
    value: number;
    var: number;
  };
};

export type StatsigSeriesResults = {
  [seriesName: string]: StatsigResults;
};
